import {
  Box,
  Heading,
  Icon,
  Image,
  RadioGroup,
  Tile,
  VisuallyHidden,
  forwardRef,
  useDisclosure,
  useRadio,
} from '@hover/blueprint';
import type { BoxProps } from '@hover/blueprint';
import type { SkeletonProps, UseRadioProps } from '@hover/blueprint/chakra';
import { iCheck, iconInspiration, iconPlus } from '@hover/icons';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { LeadSheet } from 'src/modules/homeownerApp/LeadSheet/LeadSheet';
import { SimpleTile } from 'src/modules/homeownerApp/components/Customize/SimpleTile';
import { useCustomization } from 'src/modules/homeownerApp/components/Customize/useCustomization';
import { useFeaturedDisplayImages } from 'src/modules/homeownerApp/components/Embed/hooks/useFeaturedDisplayImages';
import {
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import type { HomeownerAppTrackingData } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { MY_UPLOAD_STYLE } from 'src/modules/homeownerApp/static_values';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { Route, generatePath } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';

interface StyleTile {
  style: string;
  id: number;
  thumbnailUrl: string;
  spaceType?: string | null;
}

export interface IdeaTileProps
  extends Omit<BoxProps, keyof UseRadioProps>,
    UseRadioProps,
    Pick<SkeletonProps, 'colorScheme'> {
  styleTile?: StyleTile;
  isRefreshable?: boolean;
  isSkeleton?: boolean;
  trackingData?: HomeownerAppTrackingData;
}

const IdeaTile = forwardRef<IdeaTileProps, 'div'>(
  ({ colorScheme, styleTile, isSkeleton, trackingData, ...props }, ref) => {
    const t = useHomeownerTranslate();
    const { id, style, thumbnailUrl, spaceType } = styleTile || {};
    // On slower connections, images can load with a delay. We can render
    // a hidden image element with an onload callback to render a proper loading
    // state until the image is cached in the browser.
    const [isImageLoading, setIsImageLoading] = useState(true);
    const { aiDisplayImageId } = useParams<{ aiDisplayImageId: string }>();
    const history = useHistory();

    const onClick = () => {
      if (!id) return;

      buttonPressed({
        ...trackingData,
        button_text: style || 'Unknown',
        style: style || undefined,
        space_type: spaceType || undefined,
        track_id: 'sample-design-style-ideas',
      });
      history.push(
        generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
          aiDisplayImageId: id,
        }),
      );
    };

    const { getLabelProps, getRootProps, getInputProps } = useRadio({
      ...props,
      value: String(id),
    });

    return (
      <Tile
        as="label"
        colorScheme={colorScheme}
        isLoading={isSkeleton || isImageLoading}
        isSelected={String(id) === aiDisplayImageId}
        label={
          style === MY_UPLOAD_STYLE ? t('fullscreenImage.original') : style
        }
        labelProps={getLabelProps()}
        onClick={onClick}
        selectionIndicator={<Icon icon={iCheck} />}
        tileProps={{
          bgImage: thumbnailUrl ? `url('${thumbnailUrl}')` : undefined,
          height: 1,
        }}
        {...getRootProps()}
        {...props}
        ref={ref}
      >
        <VisuallyHidden>
          <input {...getInputProps()} />
          {!isSkeleton && (
            <Image
              alt="Hidden Image"
              onLoad={() => setIsImageLoading(false)}
              src={thumbnailUrl}
            />
          )}
        </VisuallyHidden>
      </Tile>
    );
  },
);

export const CustomizeDesignIdeas = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { targetImage: image } = useCustomization();
  const { featuredDisplayImages, supportedStylesAndOriginal, loading } =
    useFeaturedDisplayImages(image.aiOriginalImageId);
  const { trackingData } = useHomeownerAppContext();
  const {
    isOpen: isLeadSheetOpen,
    onClose: onLeadSheetClose,
    onToggle: onLeadSheetToggle,
  } = useDisclosure();

  const { hasLeadForm, ctaLink } = useEmbedContext();

  const onPressViewMore = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'View more ideas',
      space_type: image.spaceType || undefined,
      style: image.style || undefined,
      track_id: 'samples-view-more',
    });

    if (hasLeadForm) {
      onLeadSheetToggle();
    } else {
      window.open(ctaLink, '_blank');
    }
  };

  // filter these for unique styles, only display up to 8 as 9th tile is link to /designer
  const styles = featuredDisplayImages
    ?.map(aiDisplayImage => ({
      style: aiDisplayImage.style,
      id: aiDisplayImage.id,
      thumbnailUrl: aiDisplayImage.aiGeneratedImage?.thumbnailUrl,
      spaceType: aiDisplayImage.spaceType,
    }))
    .slice(0, 8);
  const stylesLoading = loading || !styles || styles.length === 0;

  // We get back the supported styles before the active image logic and
  // filtering returns a result. We can use the supported styles to
  // render a "suspense" like effect on the tile components until the hook resolves
  const optimisticStyles = supportedStylesAndOriginal.slice(0, 8);

  return (
    <Box flexDirection="column" marginX={700} marginY={700} maxHeight={1}>
      <Box
        alignContent="center"
        alignItems="center"
        flexDirection="row"
        pb={500}
      >
        <Heading as="h1" color="inherit" noMargin size={600}>
          {t('embed.designIdeas')}
        </Heading>
        <Icon icon={iconInspiration} pl={100} size="medium" />
      </Box>
      <RadioGroup
        display="grid"
        gap={400}
        gridAutoFlow="row dense"
        gridAutoRows="minmax(125px, auto)"
        gridTemplateColumns="repeat(3, 1fr)"
      >
        {stylesLoading
          ? optimisticStyles.map((style: string) => (
              <IdeaTile isSkeleton key={`design-ideas-tile-${style}`} />
            ))
          : styles.map(style => (
              <IdeaTile
                key={`design-ideas-tile-${style.style}`}
                styleTile={style}
                trackingData={trackingData}
              />
            ))}
        <SimpleTile
          onClick={onPressViewMore}
          text={t('embed.exploreMore')}
          tileProps={{
            height: { base: 500, tablet: 1 },
          }}
        >
          <Icon icon={iconPlus} size="large" />
        </SimpleTile>
      </RadioGroup>

      <LeadSheet isOpen={isLeadSheetOpen} onClose={onLeadSheetClose} />
    </Box>
  );
};
